import React, { useState } from 'react';
import './Pricing.css';

interface Module {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  priceModel: 'fixed' | 'per-trial' | 'per-patient' | 'per-site';
  category: 'core' | 'ai' | 'automation' | 'analytics';
  features: string[];
  techSpecs: {
    label: string;
    value: string;
  }[];
}

const modules: Module[] = [
  {
    id: 'ai-parser',
    name: 'AI Study Preparation',
    description: 'Advanced LLMs extract and structure protocol metadata with human-level comprehension',
    basePrice: 2500,
    priceModel: 'per-trial',
    category: 'ai',
    features: [
      'Protocol parsing & extraction',
      'Metadata structuring',
      'Regulatory requirement mapping',
      'Risk assessment automation'
    ],
    techSpecs: [
      { label: 'Technology', value: 'Anthropic + LangChain' },
      { label: 'Accuracy', value: '99.2%' },
      { label: 'Processing', value: '< 30 seconds' }
    ]
  },
  {
    id: 'smart-matching',
    name: 'Smart Matching Engine',
    description: 'AI-powered semantic similarity algorithms match protocols with optimal resources',
    basePrice: 1800,
    priceModel: 'per-trial',
    category: 'ai',
    features: [
      'CRO matching & ranking',
      'Site selection optimization',
      'Patient pool analysis',
      'Compatibility scoring'
    ],
    techSpecs: [
      { label: 'Algorithm', value: 'Vector Similarity' },
      { label: 'Match Quality', value: '95%' },
      { label: 'Speed', value: 'Real-time' }
    ]
  },
  {
    id: 'compliance-engine',
    name: 'Compliance Engine',
    description: 'Automated regulatory validation for all LATAM countries with real-time monitoring',
    basePrice: 3200,
    priceModel: 'per-trial',
    category: 'core',
    features: [
      'Multi-country compliance',
      'Real-time monitoring',
      'Automated reporting',
      'Regulatory updates'
    ],
    techSpecs: [
      { label: 'Coverage', value: '12 Countries' },
      { label: 'Monitoring', value: 'Real-time' },
      { label: 'Updates', value: 'Live' }
    ]
  },
  {
    id: 'smart-contracts',
    name: 'Smart Contracts',
    description: 'Blockchain-based milestone tracking with automated payments and audit trails',
    basePrice: 2200,
    priceModel: 'per-trial',
    category: 'automation',
    features: [
      'Milestone automation',
      'Payment processing',
      'Audit trail management',
      'Contract templates'
    ],
    techSpecs: [
      { label: 'Network', value: 'Ethereum + L2' },
      { label: 'Security', value: 'Immutable' },
      { label: 'Payments', value: 'Automated' }
    ]
  },
  {
    id: 'voice-ai',
    name: 'Voice AI Recruitment',
    description: 'Intelligent voice agents conduct patient screening with natural conversation flow',
    basePrice: 850,
    priceModel: 'per-patient',
    category: 'ai',
    features: [
      'Automated screening calls',
      'Natural conversation',
      'Multi-language support',
      'Follow-up automation'
    ],
    techSpecs: [
      { label: 'Technology', value: 'VAPI Integration' },
      { label: 'Availability', value: '24/7' },
      { label: 'Conversion', value: '85%' }
    ]
  },
  {
    id: 'dashboards',
    name: 'Real-time Dashboards',
    description: 'Live insights with recruitment health monitoring and predictive analytics',
    basePrice: 1200,
    priceModel: 'fixed',
    category: 'analytics',
    features: [
      'Real-time monitoring',
      'Predictive analytics',
      'Custom reporting',
      'Alert system'
    ],
    techSpecs: [
      { label: 'Updates', value: 'Real-time' },
      { label: 'Analytics', value: 'Predictive' },
      { label: 'Alerts', value: 'Proactive' }
    ]
  }
];

const Pricing: React.FC = () => {
  const [selectedModules, setSelectedModules] = useState<string[]>(['compliance-engine']);
  const [trialCount, setTrialCount] = useState(1);
  const [patientCount, setPatientCount] = useState(100);
  const [siteCount, setSiteCount] = useState(5);

  const toggleModule = (moduleId: string) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const calculatePrice = () => {
    return selectedModules.reduce((total, moduleId) => {
      const module = modules.find(m => m.id === moduleId);
      if (!module) return total;

      switch (module.priceModel) {
        case 'fixed':
          return total + module.basePrice;
        case 'per-trial':
          return total + (module.basePrice * trialCount);
        case 'per-patient':
          return total + (module.basePrice * patientCount);
        case 'per-site':
          return total + (module.basePrice * siteCount);
        default:
          return total;
      }
    }, 0);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'core': return 'var(--color-primary)';
      case 'ai': return 'var(--color-neural)';
      case 'automation': return 'var(--color-secondary)';
      case 'analytics': return 'var(--color-tech)';
      default: return 'var(--color-accent)';
    }
  };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="hero-background">
          <div className="pricing-grid-bg">
            {[...Array(20)].map((_, i) => (
              <div key={i} className={`grid-cell cell-${i}`}></div>
            ))}
          </div>
          <div className="cost-streams">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`cost-stream stream-${i}`}></div>
            ))}
          </div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <div className="section-badge pricing">
              <span className="badge-icon">💰</span>
              <span>PAAS MODULAR PRICING</span>
            </div>
            
            <h1>
              <span className="gradient-text">Build Your Perfect</span>
              <br />
              Clinical Trial Stack
            </h1>
            
            <p>
              Platform-as-a-Service modular approach. Select only the AI modules you need. 
              Scale up or down based on your trial requirements. No vendor lock-in.
            </p>
            
            <div className="paas-benefits">
              <div className="benefit-item">
                <div className="benefit-icon">🔧</div>
                <span>Modular Architecture</span>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">📈</div>
                <span>Pay-as-you-Scale</span>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">🔄</div>
                <span>No Lock-in</span>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">⚡</div>
                <span>Instant Deployment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="module-selector">
        <div className="container">
          <div className="selector-header">
            <div className="section-badge modules">
              <span className="badge-icon">⚙️</span>
              <span>MODULE SELECTION</span>
            </div>
            <h2>
              <span className="gradient-text">Choose Your</span> AI Modules
            </h2>
            <p>
              Select the specific modules that match your clinical trial needs. 
              Each module is independently deployable and scalable.
            </p>
          </div>
          
          <div className="modules-grid">
            {modules.map((module) => (
              <div 
                key={module.id}
                className={`module-card ${selectedModules.includes(module.id) ? 'selected' : ''}`}
                onClick={() => toggleModule(module.id)}
                style={{ '--category-color': getCategoryColor(module.category) } as React.CSSProperties}
              >
                <div className="module-header">
                  <div className="module-selector-checkbox">
                    <div className={`checkbox ${selectedModules.includes(module.id) ? 'checked' : ''}`}>
                      {selectedModules.includes(module.id) && <span className="checkmark">✓</span>}
                    </div>
                  </div>
                  <div className="module-category">
                    <span className="category-badge">{module.category.toUpperCase()}</span>
                  </div>
                </div>
                
                <div className="module-content">
                  <h3>{module.name}</h3>
                  <p>{module.description}</p>
                  
                  <div className="module-pricing">
                    <div className="price-display">
                      <span className="price-amount">${module.basePrice.toLocaleString()}</span>
                      <span className="price-model">/{module.priceModel.replace('-', ' ')}</span>
                    </div>
                  </div>
                  
                  <div className="module-features">
                    <h4>Features:</h4>
                    <ul>
                      {module.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="module-specs">
                    <h4>Tech Specs:</h4>
                    <div className="specs-grid">
                      {module.techSpecs.map((spec, index) => (
                        <div key={index} className="spec-item">
                          <span className="spec-label">{spec.label}:</span>
                          <span className="spec-value">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="module-visual">
                  <div className="module-icon">
                    {module.category === 'ai' && '🧠'}
                    {module.category === 'core' && '🛡️'}
                    {module.category === 'automation' && '⚙️'}
                    {module.category === 'analytics' && '📊'}
                  </div>
                  <div className="selection-indicator"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-calculator">
        <div className="container">
          <div className="calculator-content">
            <div className="calculator-controls">
              <div className="section-badge calculator">
                <span className="badge-icon">🧮</span>
                <span>PRICING CALCULATOR</span>
              </div>
              
              <h2>
                <span className="gradient-text">Configure Your</span> Requirements
              </h2>
              
              <div className="controls-grid">
                <div className="control-group">
                  <label>Active Trials</label>
                  <div className="control-input">
                    <input 
                      type="range" 
                      min="1" 
                      max="20" 
                      value={trialCount}
                      onChange={(e) => setTrialCount(parseInt(e.target.value))}
                    />
                    <span className="control-value">{trialCount}</span>
                  </div>
                </div>
                
                <div className="control-group">
                  <label>Target Patients</label>
                  <div className="control-input">
                    <input 
                      type="range" 
                      min="50" 
                      max="1000" 
                      step="50"
                      value={patientCount}
                      onChange={(e) => setPatientCount(parseInt(e.target.value))}
                    />
                    <span className="control-value">{patientCount}</span>
                  </div>
                </div>
                
                <div className="control-group">
                  <label>Research Sites</label>
                  <div className="control-input">
                    <input 
                      type="range" 
                      min="1" 
                      max="50" 
                      value={siteCount}
                      onChange={(e) => setSiteCount(parseInt(e.target.value))}
                    />
                    <span className="control-value">{siteCount}</span>
                  </div>
                </div>
              </div>
              
              <div className="selected-modules-summary">
                <h3>Selected Modules ({selectedModules.length})</h3>
                <div className="modules-list">
                  {selectedModules.map(moduleId => {
                    const module = modules.find(m => m.id === moduleId);
                    return module ? (
                      <div key={moduleId} className="selected-module">
                        <span className="module-name">{module.name}</span>
                        <span className="module-price">
                          ${module.basePrice.toLocaleString()}/{module.priceModel.replace('-', ' ')}
                        </span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
            
            <div className="pricing-result">
              <div className="result-visual">
                <div className="price-calculator-display">
                  <div className="calculator-screen">
                    <div className="calculation-steps">
                      {selectedModules.map(moduleId => {
                        const module = modules.find(m => m.id === moduleId);
                        if (!module) return null;
                        
                        let multiplier = 1;
                        switch (module.priceModel) {
                          case 'per-trial': multiplier = trialCount; break;
                          case 'per-patient': multiplier = patientCount; break;
                          case 'per-site': multiplier = siteCount; break;
                        }
                        
                        return (
                          <div key={moduleId} className="calc-step">
                            <span className="step-module">{module.name}</span>
                            <span className="step-calculation">
                              ${module.basePrice.toLocaleString()} × {multiplier}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="total-calculation">
                      <div className="total-line"></div>
                      <div className="total-amount">
                        <span className="total-label">Monthly Total:</span>
                        <span className="total-value">${calculatePrice().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="calculator-indicators">
                    <div className="indicator active"></div>
                    <div className="indicator active"></div>
                    <div className="indicator"></div>
                  </div>
                </div>
              </div>
              
              <div className="result-actions">
                <button className="primary pricing-cta">
                  <span className="button-icon">🚀</span>
                  <span className="button-text">
                    <span className="button-title">Start Free Trial</span>
                    <span className="button-subtitle">30-day pilot program</span>
                  </span>
                </button>
                
                <button className="secondary pricing-demo">
                  <span className="button-icon">📞</span>
                  <span className="button-text">
                    <span className="button-title">Book Demo</span>
                    <span className="button-subtitle">Custom configuration</span>
                  </span>
                </button>
              </div>
              
              <div className="pricing-notes">
                <div className="note-item">
                  <span className="note-icon">💡</span>
                  <span>Volume discounts available for 10+ trials</span>
                </div>
                <div className="note-item">
                  <span className="note-icon">🔒</span>
                  <span>Enterprise security & compliance included</span>
                </div>
                <div className="note-item">
                  <span className="note-icon">📞</span>
                  <span>24/7 technical support included</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="paas-advantages">
        <div className="container">
          <div className="section-header">
            <div className="section-badge advantages">
              <span className="badge-icon">⭐</span>
              <span>PAAS ADVANTAGES</span>
            </div>
            <h2>
              <span className="gradient-text">Why Choose</span> Lummo PaaS?
            </h2>
            <p>
              Platform-as-a-Service architecture designed specifically for clinical trial complexity 
              and regulatory requirements across Latin America.
            </p>
          </div>
          
          <div className="advantages-grid">
            <div className="advantage-card">
              <div className="advantage-visual">
                <div className="modular-blocks">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className={`block block-${i}`}></div>
                  ))}
                </div>
              </div>
              <div className="advantage-content">
                <h3>🔧 Modular Architecture</h3>
                <p>Deploy only what you need. Add or remove modules as your trials evolve. No monolithic platform constraints.</p>
                <div className="advantage-stats">
                  <div className="stat">
                    <span className="stat-value">6</span>
                    <span className="stat-label">Independent Modules</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">100%</span>
                    <span className="stat-label">API Compatible</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-visual">
                <div className="scaling-chart">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className={`scale-bar bar-${i}`}></div>
                  ))}
                </div>
              </div>
              <div className="advantage-content">
                <h3>📈 Elastic Scaling</h3>
                <p>Automatically scales with your trial volume. Pay only for active usage. No upfront infrastructure costs.</p>
                <div className="advantage-stats">
                  <div className="stat">
                    <span className="stat-value">0-1000</span>
                    <span className="stat-label">Patients/Month</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">99.9%</span>
                    <span className="stat-label">Uptime SLA</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-visual">
                <div className="integration-hub">
                  <div className="hub-center"></div>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className={`integration-node node-${i}`}></div>
                  ))}
                </div>
              </div>
              <div className="advantage-content">
                <h3>🔗 Seamless Integration</h3>
                <p>RESTful APIs and webhooks for easy integration with existing systems. No data migration required.</p>
                <div className="advantage-stats">
                  <div className="stat">
                    <span className="stat-value">50+</span>
                    <span className="stat-label">API Endpoints</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">&lt; 24h</span>
                    <span className="stat-label">Integration Time</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-visual">
                <div className="compliance-matrix">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className={`compliance-cell cell-${i} active`}></div>
                  ))}
                </div>
              </div>
              <div className="advantage-content">
                <h3>🛡️ Compliance-First</h3>
                <p>Built-in compliance for all LATAM regulations. Automatic updates and audit trails included.</p>
                <div className="advantage-stats">
                  <div className="stat">
                    <span className="stat-value">12</span>
                    <span className="stat-label">Countries Covered</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">100%</span>
                    <span className="stat-label">Audit Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing; 